"use client";
import { zodResolver } from "@hookform/resolvers/zod";
import { QueryClient, QueryClientProvider, useQuery as useReactQuery } from "@tanstack/react-query";
import destr from "destr";
import { useLocale, useTranslations } from "next-intl";
import { useQueryState } from "nuqs";
import { ofetch } from "ofetch";
import { useForm } from "react-hook-form";
import sanitizeHtml from "sanitize-html";
import * as z from "zod";
import { ErrorElement } from "@/components/ErrorElement";
import { LoadingElement } from "@/components/LoadingElement";
import { Button } from "@/components/ui/button";
import * as RadixForm from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { env } from "@/env";
import Link from "@/lib/locale/Link";
import { KeywordChart } from "@/lib/podcast-insights/Charts";
import { ResultItemSoundComponent } from "@/lib/podcast-insights/Components";
import { highlightMatch } from "@/lib/podcast-insights/utils";
import { blockedPodcastIds } from "./visibility";

const queryClient = new QueryClient();

export const SearchPodcastsSegmentsComponent = ({ podcastId }: { podcastId?: string }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <SearchPodcastsSegmentsComponentContent podcastId={podcastId} />
    </QueryClientProvider>
  );
};

const SearchPodcastsSegmentsComponentContent = ({ podcastId }: { podcastId?: string }) => {
  const [paramsQuery, setParamsQuery] = useQueryState("query", {
    defaultValue: "",
  });
  const t = useTranslations();
  const formSchema = z.object({
    query: z.string().min(1),
  });
  const form = useForm({
    defaultValues: { query: paramsQuery || "" },
    resolver: zodResolver(formSchema),
  });

  const handleSubmitForm = async (formData: z.infer<typeof formSchema>) => {
    setParamsQuery(formData.query);
  };

  const {
    data: segmentResults,
    isFetching: segmentsFetching,
    isError: segmentsError,
  } = useReactQuery({
    queryKey: ["podcast-segments", paramsQuery],
    enabled: paramsQuery.length > 1,
    select: (data) => ({
      searchResults: data.searchResults.filter(
        (row) => !blockedPodcastIds.includes(row.podcast_id)
      ),
      allPublishedAt: data.allPublishedAt.filter(
        (row) => !blockedPodcastIds.includes(row.podcast_id)
      ),
    }),
    queryFn: async () => {
      const response = ofetch<{
        searchResults: SegmentSearchResult[];
        allPublishedAt: SegmentSearchAggregateData;
      }>(`${env.NEXT_PUBLIC_API_URL}/handleSearchKeywordsInSegments`, {
        parseResponse: destr,
        query: {
          query: paramsQuery,
          podcast: podcastId,
        },
      });
      return response;
    },
  });

  return (
    <div>
      <RadixForm.Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmitForm)} className="space-y-2">
          <div className="sm:flex sm:items-end w-full">
            <div className="grow">
              <RadixForm.FormField
                control={form.control}
                name="query"
                render={({ field }) => (
                  <RadixForm.FormItem>
                    <RadixForm.FormLabel>
                      {t("podcasts.searchAllEpisodesLabel")}
                    </RadixForm.FormLabel>
                    <RadixForm.FormControl>
                      <Input type="search" {...field} />
                    </RadixForm.FormControl>
                    <RadixForm.FormMessage />
                  </RadixForm.FormItem>
                )}
              />
            </div>
            <Button
              type="submit"
              className="ml-2"
              disabled={segmentsFetching}
              loading={segmentsFetching}
            >
              {t("podcasts.searchButton")}
            </Button>
          </div>
        </form>
      </RadixForm.Form>
      <div className="mt-4">
        {(function () {
          if (!paramsQuery) {
            return null;
          }
          if (segmentsFetching) {
            return <LoadingElement />;
          } else if (segmentsError || !segmentResults) {
            return <ErrorElement />;
          }
          const nHits = segmentResults.allPublishedAt.length;
          const hasHits = nHits > 0;
          if (hasHits) {
            return (
              <div className="space-y-8">
                <div>
                  {t("search.numberOfHitsOn", {
                    query: paramsQuery,
                    hits: nHits,
                  })}
                </div>
                <SearchSegmentList segments={segmentResults.searchResults} query={paramsQuery} />
                <KeywordChart
                  aggregateData={segmentResults.allPublishedAt}
                  className="h-[320] w-full"
                />
              </div>
            );
          } else {
            return (
              <div>
                {t("search.numberOfHitsOn", {
                  query: paramsQuery,
                  hits: nHits,
                })}
              </div>
            );
          }
        })()}
      </div>
    </div>
  );
};

const SearchSegmentList = ({
  segments,
  query,
}: {
  segments: SegmentSearchResult[];
  query?: string;
}) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {segments.map((segment) => {
        return <SearchSegmentsItem key={segment.segment_id} segment={segment} query={query} />;
      })}
    </div>
  );
};

const SearchSegmentsItem = ({
  segment,
  query,
}: {
  segment: SegmentSearchResult;
  query?: string;
}) => {
  const t = useTranslations();
  const locale = useLocale();

  const excerptHtml = sanitizeHtml(
    query
      ? highlightMatch({
          text: segment.segment_text,
          query,
        })
      : segment.segment_text,
    {
      allowedTags: ["b"],
    }
  );

  let dateToShow = new Date(segment.published_at);
  if (dateToShow.getTime() === 0) {
    dateToShow = new Date(segment.episode_created_at);
  }

  return (
    <div className="border p-4 flex flex-col justify-between rounded-lg">
      <div>
        <div className="flex justify-between">
          <p className="uppercase text-sm">{segment.podcast_title}</p>
          <p className="uppercase text-sm">{dateToShow.toLocaleDateString(locale)}</p>
        </div>
        <h3 className="scroll-m-20">{segment.episode_title}</h3>
        <p className="uppercase text-sm mt-2">{t("podcasts.excerpt")}</p>
        <p className="text-sm" dangerouslySetInnerHTML={{ __html: excerptHtml }}></p>
        <Link
          key={segment.episode_id}
          href={`/podcasts/${segment.podcast_id}/${segment.episode_id}?query=${query}`}
          className="text-primary uppercase text-lg mt-4 hover:text-primary/90 transition-colors duration-200 ease-in-out"
        >
          {t("podcasts.episodeLinkText")}
        </Link>
      </div>
      <div className="mt-4">
        <ResultItemSoundComponent searchResult={segment} />
      </div>
    </div>
  );
};
